import { useEffect, useMemo, useRef, useState } from 'react';
import { type thModel } from '../../utils/types';

const getStorageKey = (tableId: string) => `trader-table-columns:${tableId}`;

export const useColumnVisibility = (tableId: string | undefined, thArray: thModel[]) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<boolean[]>(() => thArray.map(() => true));
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fallback = thArray.map(() => true);
    if (!tableId) {
      setVisibleColumns(fallback);
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(getStorageKey(tableId));
      if (!rawValue) {
        setVisibleColumns(fallback);
        return;
      }

      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) {
        setVisibleColumns(fallback);
        return;
      }

      setVisibleColumns(
        thArray.map((_, index) => (typeof parsed[index] === 'boolean' ? parsed[index] : true)),
      );
    } catch {
      setVisibleColumns(fallback);
    }
  }, [tableId, thArray]);

  useEffect(() => {
    if (!tableId) {
      return;
    }

    window.localStorage.setItem(getStorageKey(tableId), JSON.stringify(visibleColumns));
  }, [tableId, visibleColumns]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const visibleCount = useMemo(() => visibleColumns.filter(Boolean).length, [visibleColumns]);

  const toggleColumn = (index: number) => {
    setVisibleColumns((prev) => {
      if (prev[index] && visibleCount === 1) {
        return prev;
      }

      return prev.map((value, currentIndex) =>
        currentIndex === index ? !value : value,
      );
    });
  };

  const filteredThArray = useMemo(
    () => thArray.filter((_, index) => visibleColumns[index] ?? true),
    [thArray, visibleColumns],
  );

  const filterRow = <T,>(row: T[]) => row.filter((_, index) => visibleColumns[index] ?? true);

  return {
    isOpen,
    setIsOpen,
    pickerRef,
    visibleColumns,
    visibleCount,
    filteredThArray,
    filterRow,
    toggleColumn,
  };
};
